import { Checkbox, DropdownEditor, FocusImplementer, NumberEditor, TextEditor } from '@approvalmax/ui';
import { intl, mathService, numberHelpers } from '@approvalmax/utils';
import { constants } from 'modules/common';
import { domain } from 'modules/data';
import { PlainDataProvider } from 'modules/data-providers';
import { CellIndicator, ReadOnlyCell } from 'pages/requestForm/components';
import { NetSuiteItem, NetSuiteSalesTaxItem } from 'pages/requestForm/data/netsuite/NetSuiteContext';

import { ColumnDefinition } from '../../../../selectors/types/ColumnDefinition';
import { NetSuitePriceLevel } from '../../data/types';
import { NetSuiteInvoiceListItemExpanded } from '../../data/types/NetSuiteInvoiceItems';
import { setCurrencyPrecision } from '../../helpers';
import {
    isFieldMandatory,
    isFieldReadOnly,
    isFieldVisible,
} from '../../NetSuiteRequestForm/NetSuiteRequestForm.helpers';
import { NetSuiteLineItemDropdownCell } from '../NetSuiteLineItemDropdownCell/NetSuiteLineItemDropdownCell';
import { NetSuiteLineItemItemCell } from '../NetSuiteLineItemItemCell/NetSuiteLineItemItemCell';
import { NetSuiteLineItemTaxCodeCell } from '../NetSuiteLineItemTaxCodeCell/NetSuiteLineItemTaxCodeCell';
import {
    isAmountFieldDisabledByPriceLevel,
    isUnitPriceFieldDisabledByPriceLevel,
} from './NetSuiteInvoiceItemList.helpers';
import { messages } from './NetSuiteInvoiceItemList.messages';
import { DropdownEditorContainer, StyledPriceCheckIndicator } from './NetSuiteInvoiceItemList.styles';
import { ExtendedColumnDefinition, InvoiceItemListColumnOptions } from './NetSuiteInvoiceItemList.types';

const { netSuiteConstants } = constants;

export const getColumnDefinitions = ({
    showErrors,
    showErrorsOnEmptyLine,
    fieldSettings,
    classes,
    departments,
    locations,
    isLoadingPriceLevels,
    isApproverMode,
    currencyPrecision,
    customFieldsColumnDefinition,
    onItemChange,
    onPriceLevelChange,
    onFieldUpdate,
    onMultiLineFieldUpdate,
    updateLineItem,
}: InvoiceItemListColumnOptions): ColumnDefinition<NetSuiteInvoiceListItemExpanded>[] => {
    const columnDef: ExtendedColumnDefinition[] = [
        ...(isApproverMode
            ? []
            : [
                  {
                      id: 'checkbox',
                      name: '',
                      fixedWidth: 40,
                      headerCell: (lineItems) => {
                          const checked = lineItems.every((line) => line.checked);
                          const triState = lineItems.some((line) => line.checked);

                          const handleClick = () => {
                              const nextState = !checked;

                              onMultiLineFieldUpdate('checked', nextState);
                          };

                          return (
                              <div onClick={handleClick}>
                                  <Checkbox size='size-18' triState={triState} checked={checked} />
                              </div>
                          );
                      },
                      cell: (lineItem) => {
                          return (
                              <div
                                  onClick={() => {
                                      onFieldUpdate(lineItem.id, 'checked', !lineItem.checked);
                                  }}
                              >
                                  <Checkbox checked={lineItem.checked} size='size-18' />
                              </div>
                          );
                      },
                  } as ColumnDefinition<NetSuiteInvoiceListItemExpanded>,
              ]),
        {
            id: 'item',
            name: messages.itemColumnName,
            fixedWidth: 274,
            isHidden: !isFieldVisible(fieldSettings.itemsItem),
            readonly: () => isFieldReadOnly(fieldSettings.itemsItem),
            invalid: (li) =>
                showErrors &&
                ((isFieldMandatory(fieldSettings.itemsItem) && !li.isEmpty && !li.item) || showErrorsOnEmptyLine),
            cell: (li, { invalid, readonly }) =>
                readonly ? (
                    <ReadOnlyCell text={li.item?.text ?? ''} />
                ) : (
                    <DropdownEditorContainer>
                        <NetSuiteLineItemItemCell
                            invalid={invalid}
                            value={li.item}
                            singleLinePerItem={false}
                            onChange={(value: NetSuiteItem | null) => {
                                onItemChange(li.id, value);
                            }}
                            placeholder={messages.itemFieldPlaceholder}
                        />
                    </DropdownEditorContainer>
                ),
        },
        {
            id: 'description',
            name: messages.descriptionColumnName,
            fixedWidth: 274,
            isHidden: !isFieldVisible(fieldSettings.itemsDescription),
            readonly: () => isFieldReadOnly(fieldSettings.itemsDescription),
            invalid: (li) =>
                showErrors &&
                ((isFieldMandatory(fieldSettings.itemsDescription) && !li.isEmpty && !li.description) ||
                    showErrorsOnEmptyLine),
            cell: (li, { invalid, readonly }) =>
                readonly ? (
                    <ReadOnlyCell text={li.description ?? ''} />
                ) : (
                    <FocusImplementer>
                        {({ registerFocusable }) => (
                            <TextEditor
                                ref={registerFocusable}
                                invalid={invalid}
                                value={li.description ?? ''}
                                onChange={(value) => {
                                    onFieldUpdate(li.id, 'description', value);
                                }}
                                placeholder={messages.descriptionFieldPlaceholder}
                                theme='transparent'
                                maxLength={netSuiteConstants.descriptionMaxLength}
                                changeOnBlur
                            />
                        )}
                    </FocusImplementer>
                ),
        },
        {
            id: 'quantity',
            name: messages.quantityColumnName,
            alignRight: true,
            fixedWidth: 160,
            isHidden: !isFieldVisible(fieldSettings.itemsQuantity),
            readonly: () => isFieldReadOnly(fieldSettings.itemsQuantity),
            invalid: (li) =>
                showErrors &&
                ((isFieldMandatory(fieldSettings.itemsQuantity) &&
                    !li.isEmpty &&
                    !numberHelpers.isNumber(li.quantity)) ||
                    showErrorsOnEmptyLine),
            cell: (li, { invalid, readonly }) =>
                readonly ? (
                    <ReadOnlyCell text={li.quantity !== null ? intl.formatNumber(li.quantity, 'auto') : ''} />
                ) : (
                    <FocusImplementer>
                        {({ registerFocusable }) => (
                            <>
                                <NumberEditor
                                    ref={registerFocusable}
                                    allowFloat
                                    invalid={invalid}
                                    value={li.quantity !== null ? li.quantity : null}
                                    onChange={(newValue: number | null) => {
                                        onFieldUpdate(li.id, 'quantity', newValue);
                                    }}
                                    placeholder={messages.quantityFieldPlaceholder}
                                    theme='transparent'
                                    align='right'
                                    max={netSuiteConstants.quantityMaxValue}
                                    min={0}
                                    precision={5}
                                    displayFormat='currency'
                                    changeOnBlur={false}
                                />
                            </>
                        )}
                    </FocusImplementer>
                ),
        },
        {
            id: 'units',
            name: messages.unitsColumnName,
            fixedWidth: 160,
            isHidden: !isFieldVisible(fieldSettings.itemsUnit),
            readonly: () => isFieldReadOnly(fieldSettings.itemsUnit),
            invalid: (li) =>
                showErrors &&
                ((isFieldMandatory(fieldSettings.itemsUnit) && !li.isEmpty && !li.units) || showErrorsOnEmptyLine),
            cell: (li, { invalid, readonly }) =>
                readonly ? (
                    <ReadOnlyCell text={li.units?.text ?? ''} />
                ) : (
                    <FocusImplementer>
                        {({ registerFocusable }) => (
                            <DropdownEditorContainer>
                                <PlainDataProvider items={li.unitList ?? []}>
                                    <DropdownEditor
                                        ref={registerFocusable}
                                        invalid={invalid}
                                        value={li.units}
                                        singleLinePerItem={false}
                                        onChange={(value: domain.NetSuiteUnitItem) => {
                                            updateLineItem(li.id, {
                                                units: value,
                                            });
                                        }}
                                        placeholder={messages.unitsFieldPlaceholder}
                                        theme='transparent'
                                    />
                                </PlainDataProvider>
                            </DropdownEditorContainer>
                        )}
                    </FocusImplementer>
                ),
        },
        {
            id: 'priceLevel',
            name: messages.priceLevelColumnName,
            fixedWidth: 200,
            isHidden: !isFieldVisible(fieldSettings.itemsPriceLevel),
            readonly: () => isFieldReadOnly(fieldSettings.itemsPriceLevel),
            invalid: (li) =>
                showErrors &&
                ((isFieldMandatory(fieldSettings.itemsPriceLevel) && !li.isEmpty && !li.priceLevel) ||
                    showErrorsOnEmptyLine),
            cell: (li, { invalid, readonly }) =>
                readonly ? (
                    <ReadOnlyCell text={li.priceLevel?.text ?? ''} />
                ) : (
                    <FocusImplementer>
                        {({ registerFocusable }) => (
                            <DropdownEditorContainer>
                                <PlainDataProvider items={li.priceLevelList}>
                                    <DropdownEditor
                                        ref={registerFocusable}
                                        invalid={invalid}
                                        value={li.priceLevel}
                                        singleLinePerItem={false}
                                        onChange={(value: NetSuitePriceLevel | null) => {
                                            onPriceLevelChange(li.id, value);
                                        }}
                                        loading={isLoadingPriceLevels}
                                        placeholder={messages.priceLevelFieldPlaceholder}
                                        theme='transparent'
                                    />
                                </PlainDataProvider>
                            </DropdownEditorContainer>
                        )}
                    </FocusImplementer>
                ),
        },
        {
            id: 'unitPrice',
            name: messages.unitPriceColumnName,
            alignRight: true,
            fixedWidth: 160,
            isHidden: !isFieldVisible(fieldSettings.itemsUnitPrice),
            readonly: (li) => isUnitPriceFieldDisabledByPriceLevel(li, fieldSettings.itemsUnitPrice),
            invalid: (li) =>
                showErrors &&
                ((isFieldMandatory(fieldSettings.itemsUnitPrice) &&
                    !li.isEmpty &&
                    !numberHelpers.isNumber(li.unitPrice)) ||
                    showErrorsOnEmptyLine),
            cell: (li, { invalid, readonly }) =>
                readonly ? (
                    <ReadOnlyCell
                        text={li.unitPrice !== null ? intl.formatNumber(li.unitPrice, currencyPrecision) : ''}
                    />
                ) : (
                    <StyledPriceCheckIndicator
                        actualPrice={numberHelpers.isNumber(li.unitPrice) ? li.unitPrice : undefined}
                        expectedPrice={numberHelpers.isNumber(li.defaultRate) ? li.defaultRate : undefined}
                        disabled={false}
                        integrationType={domain.IntegrationType.NetSuite}
                        integrationCode={domain.IntegrationCode.NetSuiteInvoice}
                    >
                        {({ warningLevel }) => (
                            <FocusImplementer>
                                {({ registerFocusable }) => (
                                    <>
                                        <NumberEditor
                                            ref={registerFocusable}
                                            allowFloat
                                            invalid={invalid}
                                            value={li.unitPrice !== null ? li.unitPrice : null}
                                            onChange={(newValue: number | null) => {
                                                onFieldUpdate(li.id, 'unitPrice', newValue);
                                            }}
                                            placeholder={messages.unitPriceFieldPlaceholder}
                                            theme='transparent'
                                            align='right'
                                            // can be negative for discount items
                                            max={netSuiteConstants.unitPriceMaxValue}
                                            min={-netSuiteConstants.unitPriceMaxValue}
                                            precision={currencyPrecision && 8}
                                            displayFormat='currency'
                                            changeOnBlur
                                        />

                                        <CellIndicator $warningLevel={warningLevel} />
                                    </>
                                )}
                            </FocusImplementer>
                        )}
                    </StyledPriceCheckIndicator>
                ),
        },
        {
            id: 'amount',
            name: messages.amountColumnName,
            alignRight: true,
            fixedWidth: 160,
            isHidden: !isFieldVisible(fieldSettings.itemsAmount),
            readonly: (li) => isAmountFieldDisabledByPriceLevel(li, fieldSettings.itemsAmount),
            invalid: (li) => {
                if (!numberHelpers.isNumber(li.amount)) {
                    return showErrors && showErrorsOnEmptyLine;
                }

                // can be negative for discount items
                const isInvalidAmount =
                    li.amount > netSuiteConstants.maxLineItemAmount || li.amount < -netSuiteConstants.maxLineItemAmount;

                return showErrors && isInvalidAmount;
            },
            cell: (li, { invalid, readonly }) =>
                readonly ? (
                    <ReadOnlyCell text={li.amount !== null ? intl.formatNumber(li.amount, currencyPrecision) : ''} />
                ) : (
                    <FocusImplementer>
                        {({ registerFocusable }) => (
                            <NumberEditor
                                ref={registerFocusable}
                                allowFloat
                                invalid={invalid}
                                value={li.amount}
                                onChange={(newValue: number | null) => {
                                    onFieldUpdate(li.id, 'amount', newValue);
                                }}
                                placeholder={messages.amountFieldPlaceholder}
                                theme='transparent'
                                align='right'
                                // can be negative for discount items
                                max={netSuiteConstants.maxTotalAmount}
                                min={-netSuiteConstants.maxTotalAmount}
                                precision={currencyPrecision}
                                displayFormat='currency'
                                changeOnBlur
                            />
                        )}
                    </FocusImplementer>
                ),
        },
        {
            id: 'taxCode',
            name: messages.taxCodeColumnName,
            fixedWidth: 160,
            isHidden: !isFieldVisible(fieldSettings.itemsTaxCode),
            readonly: () => isFieldReadOnly(fieldSettings.itemsTaxCode),
            invalid: (li) =>
                showErrors &&
                ((isFieldMandatory(fieldSettings.itemsTaxCode) && !li.isEmpty && !li.taxCode) || showErrorsOnEmptyLine),
            cell: (li, { invalid, readonly }) =>
                readonly ? (
                    <ReadOnlyCell text={li.taxCode?.text ?? ''} />
                ) : (
                    <DropdownEditorContainer>
                        <NetSuiteLineItemTaxCodeCell
                            invalid={invalid}
                            value={li.taxCode}
                            singleLinePerItem={false}
                            onChange={(value: NetSuiteSalesTaxItem | null) => {
                                const taxAmount = setCurrencyPrecision(
                                    mathService.multiply(li.amount ?? 0, (value?.rate ?? 0) / 100),
                                    currencyPrecision
                                );
                                const grossAmount = setCurrencyPrecision(
                                    mathService.add(li.amount ?? 0, taxAmount ?? 0),
                                    currencyPrecision
                                );

                                updateLineItem(li.id, {
                                    taxAmount,
                                    grossAmount,
                                    taxCode: value,
                                    taxRate: setCurrencyPrecision(value?.rate, currencyPrecision),
                                });
                            }}
                            placeholder={messages.taxCodeFieldPlaceholder}
                        />
                    </DropdownEditorContainer>
                ),
        },
        {
            id: 'taxRate',
            name: messages.taxRateColumnName,
            alignRight: true,
            fixedWidth: 160,
            isHidden: !isFieldVisible(fieldSettings.itemsTaxRate),
            readonly: () => true,
            invalid: (li) =>
                showErrors &&
                ((isFieldMandatory(fieldSettings.itemsTaxRate) && !li.isEmpty && !li.taxRate) || showErrorsOnEmptyLine),
            cell: (li) => <ReadOnlyCell text={numberHelpers.isNumber(li.taxRate) ? `${li.taxRate}%` : ''} />,
        },
        {
            id: 'taxAmount',
            name: messages.taxAmountColumnName,
            alignRight: true,
            fixedWidth: 160,
            isHidden: !isFieldVisible(fieldSettings.itemsTaxAmount),
            invalid: (li) => {
                if (!numberHelpers.isNumber(li.taxAmount)) {
                    return showErrors && showErrorsOnEmptyLine;
                }

                let isInvalidTaxAmount = false;

                // can be negative for discount items
                if (
                    li.taxAmount > netSuiteConstants.taxAmountMaxValue ||
                    li.taxAmount < -netSuiteConstants.taxAmountMaxValue
                ) {
                    isInvalidTaxAmount = true;
                } else {
                    const taxAmountDigitsCount = li.taxAmount.toString().replace(/[^\d]/g, '').length;

                    if (taxAmountDigitsCount > netSuiteConstants.taxAmountMaxDigitsCount) {
                        isInvalidTaxAmount = true;
                    }
                }

                return showErrors && isInvalidTaxAmount;
            },
            readonly: () => true,
            cell: (li) => (
                <ReadOnlyCell text={li.taxAmount !== null ? intl.formatNumber(li.taxAmount, currencyPrecision) : ''} />
            ),
        },
        {
            id: 'grossAmount',
            name: messages.grossAmountColumnName,
            alignRight: true,
            fixedWidth: 160,
            invalid: (li) => showErrors && (!numberHelpers.isNumber(li.grossAmount) || showErrorsOnEmptyLine),
            readonly: (li) => isAmountFieldDisabledByPriceLevel(li),
            cell: (li, { invalid, readonly }) =>
                readonly ? (
                    <ReadOnlyCell
                        text={li.grossAmount !== null ? intl.formatNumber(li.grossAmount, currencyPrecision) : ''}
                    />
                ) : (
                    <FocusImplementer>
                        {({ registerFocusable }) => (
                            <NumberEditor
                                ref={registerFocusable}
                                allowFloat
                                invalid={invalid}
                                value={li.grossAmount}
                                onChange={(newValue: number | null) => {
                                    onFieldUpdate(li.id, 'grossAmount', newValue);
                                }}
                                placeholder={messages.grossAmountFieldPlaceholder}
                                theme='transparent'
                                align='right'
                                // can be negative for discount items
                                max={netSuiteConstants.maxTotalAmount}
                                min={-netSuiteConstants.maxTotalAmount}
                                precision={currencyPrecision}
                                displayFormat='currency'
                                changeOnBlur
                            />
                        )}
                    </FocusImplementer>
                ),
        },
        {
            id: 'class',
            name: messages.classColumnName,
            fixedWidth: 180,
            isHidden: !isFieldVisible(fieldSettings.itemsClass),
            readonly: () => isFieldReadOnly(fieldSettings.itemsClass),
            invalid: (li) =>
                showErrors &&
                ((isFieldMandatory(fieldSettings.itemsClass) && !li.class && !li.isEmpty) || showErrorsOnEmptyLine),
            cell: (li, { invalid, readonly }) =>
                readonly ? (
                    <ReadOnlyCell text={li.class?.text ?? ''} />
                ) : (
                    <DropdownEditorContainer>
                        <NetSuiteLineItemDropdownCell
                            items={classes}
                            invalid={invalid}
                            value={li.class}
                            singleLinePerItem={false}
                            onChange={(value: domain.NetSuiteItemLine['class']) => {
                                onFieldUpdate(li.id, 'class', value);
                            }}
                            placeholder={messages.classFieldPlaceholder}
                        />
                    </DropdownEditorContainer>
                ),
        },
        {
            id: 'department',
            name: messages.departmentColumnName,
            fixedWidth: 274,
            isHidden: !isFieldVisible(fieldSettings.itemsDepartment),
            readonly: () => isFieldReadOnly(fieldSettings.itemsDepartment),
            invalid: (li) =>
                showErrors &&
                ((isFieldMandatory(fieldSettings.itemsDepartment) && !li.department && !li.isEmpty) ||
                    showErrorsOnEmptyLine),
            cell: (li, { invalid, readonly }) =>
                readonly ? (
                    <ReadOnlyCell text={li.department?.text ?? ''} />
                ) : (
                    <DropdownEditorContainer>
                        <NetSuiteLineItemDropdownCell
                            items={departments}
                            invalid={invalid}
                            value={li.department}
                            singleLinePerItem={false}
                            onChange={(value: domain.NetSuiteItemLine['department']) => {
                                onFieldUpdate(li.id, 'department', value);
                            }}
                            placeholder={messages.departmentFieldPlaceholder}
                        />
                    </DropdownEditorContainer>
                ),
        },
        {
            id: 'location',
            name: messages.locationColumnName,
            fixedWidth: 180,
            isHidden: !isFieldVisible(fieldSettings.itemsLocation),
            readonly: () => isFieldReadOnly(fieldSettings.itemsLocation),
            invalid: (li) =>
                showErrors &&
                ((isFieldMandatory(fieldSettings.itemsLocation) && !li.location && !li.isEmpty) ||
                    showErrorsOnEmptyLine),
            cell: (li, { invalid, readonly }) =>
                readonly ? (
                    <ReadOnlyCell text={li.location?.text ?? ''} />
                ) : (
                    <DropdownEditorContainer>
                        <NetSuiteLineItemDropdownCell
                            items={locations}
                            invalid={invalid}
                            value={li.location}
                            singleLinePerItem={false}
                            onChange={(value: domain.NetSuiteItemLine['location']) => {
                                onFieldUpdate(li.id, 'location', value);
                            }}
                            placeholder={messages.locationFieldPlaceholder}
                        />
                    </DropdownEditorContainer>
                ),
        },
        ...customFieldsColumnDefinition,
    ];

    return columnDef.filter((col) => !col.isHidden);
};
